"use client";

import {
  CopyButton,
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  fromMm,
  MM_PER_INCH,
  POSTER_ROLES,
  type PosterInput,
  type PosterPlan,
  type PosterRole,
} from "@/lib/tool-kit/research-writing/poster-size";

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

/** The strings the results panel reads; the widget owns the English. */
export interface PosterResultsStrings {
  finalSize: string;
  metric: string;
  shape: string;
  bodyCard: string;
  sizeText: string;
  sizeMm: string;
  typeHeading: string;
  typeHead: { role: string; final: string; design: string };
  roles: Record<PosterRole, string>;
  typeNote: string;
  bodyFromGuide: string;
  bodyFromYou: string;
  guideHeading: string;
  guideHead: { source: string; size: string };
  guideRows: { ucDavis: string; scienceBuddies: string };
  guideNote: string;
  wordsHeading: string;
  fitText: string;
  ceilingText: string;
  readText: string;
  wordsNote: string;
  printSubheading: string;
  designText: string;
  fullSizeText: string;
  pptOk: string;
  pptTooBig: string;
  pptNever: string;
  imageText: string;
  imageWidthOnly: string;
  copy: string;
  copied: string;
  summaryTitle: string;
  summaryWords: string;
  summaryLines: {
    size: string;
    distance: string;
    body: string;
    design: string;
  };
  unitShort: {
    in: string;
    cm: string;
    mm: string;
    ft: string;
    m: string;
    pt: string;
  };
}

const ONE: Intl.NumberFormatOptions = { maximumFractionDigits: 1 };
const TWO: Intl.NumberFormatOptions = { maximumFractionDigits: 2 };
const FOUR: Intl.NumberFormatOptions = { maximumFractionDigits: 4 };

function percent(fraction: number): number {
  return Math.round(fraction * 100);
}

/** A length in inches written in the writer's chosen unit. */
function inUnit(inches: number, unit: PosterInput["unit"]): number {
  return fromMm(inches * MM_PER_INCH, unit);
}

function sizeStrings(
  plan: PosterPlan,
  input: PosterInput,
  s: PosterResultsStrings,
  fmt: Format
) {
  const mm = plan.size ?? { widthMm: 0, heightMm: 0 };
  return {
    inches: t(s.sizeText, {
      w: fmt(inUnit(plan.widthIn, input.unit), TWO),
      h: fmt(inUnit(plan.heightIn, input.unit), TWO),
      unit: s.unitShort[input.unit],
    }),
    metric: t(s.sizeMm, {
      w: fmt(mm.widthMm, ONE),
      h: fmt(mm.heightMm, ONE),
    }),
  };
}

function summaryText(
  plan: PosterPlan,
  input: PosterInput,
  s: PosterResultsStrings,
  fmt: Format,
  locale: string
): string {
  const size = sizeStrings(plan, input, s, fmt);
  const lines = [
    s.summaryTitle,
    t(s.summaryLines.size, { size: size.inches, mm: size.metric }),
  ];
  if (plan.feet !== null) {
    lines.push(
      t(s.summaryLines.distance, {
        distance: `${input.distance} ${s.unitShort[input.distanceUnit]}`,
      })
    );
  }
  if (plan.bodyPt !== null) {
    lines.push(t(s.summaryLines.body, { pt: fmt(plan.bodyPt) }));
  }
  if (plan.words) {
    lines.push(
      tPlural(s.summaryWords, locale, plan.words.ceiling, {
        words: fmt(plan.words.ceiling),
      })
    );
  }
  if (plan.design) {
    lines.push(
      t(s.summaryLines.design, {
        w: fmt(inUnit(plan.design.widthIn, input.unit), ONE),
        h: fmt(inUnit(plan.design.heightIn, input.unit), ONE),
        unit: s.unitShort[input.unit],
        enlarge: fmt(plan.design.enlargement),
      })
    );
  }
  return lines.join("\n");
}

function TypeSizes({
  plan,
  s,
  fmt,
}: {
  plan: PosterPlan;
  s: PosterResultsStrings;
  fmt: Format;
}) {
  if (!(plan.roles && plan.design && plan.bodyPt !== null)) {
    return null;
  }
  const { roles, design } = plan;
  const rows = POSTER_ROLES.map((role) => [
    s.roles[role],
    `${fmt(roles[role])} ${s.unitShort.pt}`,
    `${fmt(roles[role] * design.scale, ONE)} ${s.unitShort.pt}`,
  ]);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{s.typeHeading}</h3>
      <p className={HINT}>
        {t(plan.bodyFromGuide ? s.bodyFromGuide : s.bodyFromYou, {
          pt: fmt(plan.bodyPt),
        })}
      </p>
      <DataTable
        head={[s.typeHead.role, s.typeHead.final, s.typeHead.design]}
        rows={rows}
      />
      <p className={HINT}>{s.typeNote}</p>
    </div>
  );
}

function Guides({
  plan,
  input,
  s,
  fmt,
}: {
  plan: PosterPlan;
  input: PosterInput;
  s: PosterResultsStrings;
  fmt: Format;
}) {
  if (!plan.guide) {
    return null;
  }
  const distance = `${input.distance} ${s.unitShort[input.distanceUnit]}`;
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{t(s.guideHeading, { distance })}</h3>
      <DataTable
        head={[s.guideHead.source, s.guideHead.size]}
        rows={[
          [
            s.guideRows.ucDavis,
            `${fmt(Math.round(plan.guide.ucDavis))} ${s.unitShort.pt}`,
          ],
          [
            s.guideRows.scienceBuddies,
            `${fmt(Math.round(plan.guide.scienceBuddies))} ${s.unitShort.pt}`,
          ],
        ]}
      />
      <p className={HINT}>{s.guideNote}</p>
    </div>
  );
}

function Words({
  plan,
  s,
  fmt,
  locale,
  share,
}: {
  plan: PosterPlan;
  s: PosterResultsStrings;
  fmt: Format;
  locale: string;
  share: number;
}) {
  if (!(plan.words && plan.bodyPt !== null)) {
    return null;
  }
  const { fit, ceiling, minutes } = plan.words;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{s.wordsHeading}</h3>
      <p className="text-foreground text-sm">
        {tPlural(s.fitText, locale, fit, {
          words: fmt(fit),
          share: fmt(share, { style: "percent" }),
          pt: fmt(plan.bodyPt),
        })}
      </p>
      <p className="text-foreground text-sm">
        {tPlural(s.ceilingText, locale, ceiling, { words: fmt(ceiling) })}{" "}
        {t(s.readText, { minutes: fmt(minutes, ONE) })}
      </p>
      <p className={HINT}>{s.wordsNote}</p>
    </div>
  );
}

function PowerPointNote({
  plan,
  s,
  fmt,
}: {
  plan: PosterPlan;
  s: PosterResultsStrings;
  fmt: Format;
}) {
  const design = plan.design;
  if (!design) {
    return null;
  }
  if (design.fitsPowerPoint) {
    return <p className="text-foreground text-sm">{s.pptOk}</p>;
  }
  if (design.suggestedScale === null) {
    return <p className="text-foreground text-sm">{s.pptNever}</p>;
  }
  return (
    <p className="text-foreground text-sm">
      {t(s.pptTooBig, {
        percent: fmt(percent(design.suggestedScale)),
        enlarge: fmt(100 / design.suggestedScale),
      })}
    </p>
  );
}

function imageLine(
  plan: PosterPlan,
  input: PosterInput,
  s: PosterResultsStrings,
  fmt: Format
): string | null {
  if (!plan.image) {
    return null;
  }
  const unit = s.unitShort[input.unit];
  const values = {
    w: input.imageWidth.trim(),
    h: input.imageHeight.trim(),
    unit,
    dpi: input.dpi,
    wpx: fmt(plan.image.widthPx),
    hpx: fmt(plan.image.heightPx ?? 0),
  };
  return t(
    plan.image.heightPx === null ? s.imageWidthOnly : s.imageText,
    values
  );
}

export function PosterResults({
  plan,
  input,
  strings: s,
  fmt,
  locale,
  share,
}: {
  plan: PosterPlan;
  input: PosterInput;
  strings: PosterResultsStrings;
  fmt: Format;
  locale: string;
  /** The text share as a fraction. */
  share: number;
}) {
  const size = sizeStrings(plan, input, s, fmt);
  const design = plan.design;
  const image = imageLine(plan, input, s, fmt);
  return (
    <div className="space-y-6">
      <StatGrid>
        <StatCard emphasis label={s.finalSize} value={size.inches} />
        <StatCard label={s.metric} value={size.metric} />
        <StatCard label={s.shape} value={fmt(plan.ratio, FOUR)} />
        {plan.bodyPt === null ? null : (
          <StatCard
            label={s.bodyCard}
            value={`${fmt(plan.bodyPt)} ${s.unitShort.pt}`}
          />
        )}
      </StatGrid>
      <TypeSizes fmt={fmt} plan={plan} s={s} />
      <Guides fmt={fmt} input={input} plan={plan} s={s} />
      <Words fmt={fmt} locale={locale} plan={plan} s={s} share={share} />
      {design ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{s.printSubheading}</h3>
          <p className="text-foreground text-sm">
            {design.scale === 1
              ? t(s.fullSizeText, {
                  w: fmt(inUnit(design.widthIn, input.unit), ONE),
                  h: fmt(inUnit(design.heightIn, input.unit), ONE),
                  unit: s.unitShort[input.unit],
                })
              : t(s.designText, {
                  w: fmt(inUnit(design.widthIn, input.unit), ONE),
                  h: fmt(inUnit(design.heightIn, input.unit), ONE),
                  unit: s.unitShort[input.unit],
                  percent: fmt(percent(design.scale)),
                  enlarge: fmt(design.enlargement),
                })}
          </p>
          <PowerPointNote fmt={fmt} plan={plan} s={s} />
          {image ? <p className="text-foreground text-sm">{image}</p> : null}
        </div>
      ) : null}
      <CopyButton
        copiedLabel={s.copied}
        label={s.copy}
        text={summaryText(plan, input, s, fmt, locale)}
      />
    </div>
  );
}
